import React from "react";
import { Leaf, ShoppingBag, Sparkles, Trophy, Recycle, HeartHandshake } from "lucide-react";
import { Link } from "react-router-dom";
import useSEO from "@/hooks/useSEO";

const steps = [
  { icon: <ShoppingBag className="h-6 w-6" />, title: "Discover", desc: "Browse curated sustainable products with transparent sustainability scores." },
  { icon: <Sparkles className="h-6 w-6" />, title: "Buy Smart", desc: "See the impact of your choice. Get real cashback and earn Green Points." },
  { icon: <Recycle className="h-6 w-6" />, title: "Swap Better", desc: "See greener alternatives — always know the eco-friendly upgrade." },
  { icon: <Trophy className="h-6 w-6" />, title: "Level Up", desc: "Earn badges — from Eco Beginner to Net Zero Champion." },
  { icon: <HeartHandshake className="h-6 w-6" />, title: "Give Back", desc: "Redeem points for gift cards, plant trees, or donate to NGOs." },
];

const AboutPage = () => {
  useSEO({
    title: "About Ecowallet – The AI-Powered Sustainable Shopping Companion",
    description: "Learn about Ecowallet's mission: AI-powered sustainability scores, sustainable product discovery, cashback rewards and helping everyone shop more sustainably.",
    keywords: "about ecowallet, ai sustainable shopping, sustainable shopping companion, ecowallet mission",
  });
  return (
  <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
    <div className="text-center mb-14">
      <span className="inline-flex items-center gap-2 rounded-full bg-emerald-600/10 text-emerald-700 dark:text-emerald-300 px-4 py-1.5 text-xs uppercase tracking-widest font-semibold">
        <Leaf className="h-3.5 w-3.5" /> About Ecowallet
      </span>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-6">Not another cashback site.</h1>
      <p className="text-muted-foreground mt-4 max-w-2xl mx-auto text-lg">
        Every purchase has an environmental impact. We help you make better choices while rewarding you for them —
        India's first AI-powered sustainable shopping companion.
      </p>
    </div>

    {/* Company info */}
    <div className="grid md:grid-cols-3 gap-5 mb-14" data-testid="company-info">
      <div className="card-premium p-6">
        <div className="text-xs uppercase tracking-widest text-primary font-semibold">Our Mission</div>
        <p className="mt-2 text-sm text-foreground/85 leading-relaxed">Make sustainable shopping the default choice for every Indian household — transparent, evidence-based, and rewarding.</p>
      </div>
      <div className="card-premium p-6">
        <div className="text-xs uppercase tracking-widest text-primary font-semibold">Our Approach</div>
        <p className="mt-2 text-sm text-foreground/85 leading-relaxed">AI reads publicly available product evidence; a deterministic category-specific engine turns that evidence into a comparable 0-100 sustainability score.</p>
      </div>
      <div className="card-premium p-6">
        <div className="text-xs uppercase tracking-widest text-primary font-semibold">Our Promise</div>
        <p className="mt-2 text-sm text-foreground/85 leading-relaxed">Transparency over hype. Every score shows its sources, reasoning, confidence and methodology version — and you can always contest it.</p>
      </div>
    </div>

    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {steps.map((s, i) => (
        <div key={s.title} className="card-premium p-6">
          <div className="grid place-items-center h-12 w-12 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
            {s.icon}
          </div>
          <div className="mono text-xs text-muted-foreground mt-4">STEP {i + 1}</div>
          <h3 className="text-xl font-bold mt-1">{s.title}</h3>
          <p className="text-sm text-muted-foreground mt-2">{s.desc}</p>
        </div>
      ))}
    </div>

    <div className="card-premium p-10 mt-12 text-center">
      <h3 className="text-2xl font-bold">Ready to shop with purpose?</h3>
      <Link to="/signup" className="btn-pill bg-primary text-primary-foreground mt-6 inline-flex" data-testid="about-cta">
        Get Started — it's free
      </Link>
    </div>

    {/* Contact Info */}
    <div className="mt-14 card-premium p-8" data-testid="contact-info">
      <div className="text-xs uppercase tracking-widest text-primary font-semibold">Contact Us</div>
      <h2 className="text-3xl font-bold tracking-tight mt-2">Get in touch with Ecowallet</h2>
      <p className="text-muted-foreground mt-3">Have a question about sustainability scoring, brand partnerships, or want to list a product? Reach out — we typically respond within 24 hours.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <a href="tel:+918319918203" className="flex items-center gap-4 rounded-2xl border border-border px-5 py-4 hover:border-primary hover:bg-primary/5 transition-all" data-testid="contact-phone">
          <span className="grid place-items-center h-11 w-11 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">📞</span>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Phone</div>
            <div className="mono font-semibold">+91 83199 18203</div>
          </div>
        </a>
        <a href="mailto:appecowallet@gmail.com" className="flex items-center gap-4 rounded-2xl border border-border px-5 py-4 hover:border-primary hover:bg-primary/5 transition-all" data-testid="contact-email">
          <span className="grid place-items-center h-11 w-11 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">✉️</span>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Email</div>
            <div className="font-semibold">appecowallet@gmail.com</div>
          </div>
        </a>
      </div>
    </div>
  </div>
  );
};

export default AboutPage;
